import type {
  CompanyEquipmentPatch,
  EquipmentLibraryProduct,
  EquipmentRevision,
} from '../../types/equipmentLibrary';
import { verifyEquipmentLibraryRecordDigest } from './digests';

/**
 * Resolved tenant presentation/commercial layer. The canonical product is
 * retained intact and separately accessible; no technical identity field is
 * copied into a mutable overlay object.
 */
export interface CompanyEquipmentProductView {
  canonicalProduct: EquipmentLibraryProduct;
  canonicalRevision?: EquipmentRevision;
  displayName: string;
  tags: readonly string[];
  preferredSupplierIds: readonly string[];
  privateObservationIds: readonly string[];
  privateMarketEvidenceIds: readonly string[];
  privateNotes?: string;
  active: boolean;
  appliedPatchIds: readonly string[];
}

function initialView(
  product: EquipmentLibraryProduct,
  revision?: EquipmentRevision,
): CompanyEquipmentProductView {
  return {
    canonicalProduct: product,
    ...(revision ? { canonicalRevision: revision } : {}),
    displayName: product.displayName,
    tags: [...product.tags],
    preferredSupplierIds: [],
    privateObservationIds: [],
    privateMarketEvidenceIds: [],
    active: true,
    appliedPatchIds: [],
  };
}

export function applyCompanyEquipmentPatch(
  view: CompanyEquipmentProductView,
  patch: CompanyEquipmentPatch,
  companyId: string,
): CompanyEquipmentProductView {
  const product = view.canonicalProduct;
  if (patch.companyId !== companyId) {
    throw new TypeError('Equipment patch belongs to another company.');
  }
  if (product.visibility === 'company-private' && product.companyId !== companyId) {
    throw new TypeError('Private equipment product belongs to another company.');
  }
  if (patch.targetProductId !== product.id) {
    throw new TypeError('Equipment patch targets another product.');
  }
  if (patch.baseProductDigest !== product.contentDigest) {
    throw new TypeError('Equipment patch is stale against the canonical product digest.');
  }
  if (patch.baseRevisionId) {
    if (!view.canonicalRevision) {
      throw new TypeError('Equipment patch is revision-bound but no canonical revision was supplied.');
    }
    if (patch.baseRevisionId !== view.canonicalRevision.id
      || patch.baseRevisionDigest !== view.canonicalRevision.contentDigest) {
      throw new TypeError('Equipment patch is stale against the canonical revision digest.');
    }
  }
  if (!verifyEquipmentLibraryRecordDigest('company-patch', patch)) {
    throw new TypeError('Equipment patch digest is invalid.');
  }

  return Object.freeze({
    canonicalProduct: product,
    ...(view.canonicalRevision ? { canonicalRevision: view.canonicalRevision } : {}),
    displayName: patch.patch.displayName ?? view.displayName,
    tags: Object.freeze([...(patch.patch.tags ?? view.tags)]),
    preferredSupplierIds: Object.freeze([
      ...(patch.patch.preferredSupplierIds ?? view.preferredSupplierIds),
    ]),
    privateObservationIds: Object.freeze([
      ...(patch.patch.privateObservationIds ?? view.privateObservationIds),
    ]),
    privateMarketEvidenceIds: Object.freeze([
      ...(patch.patch.privateMarketEvidenceIds ?? view.privateMarketEvidenceIds),
    ]),
    ...((patch.patch.privateNotes ?? view.privateNotes) == null
      ? {}
      : { privateNotes: patch.patch.privateNotes ?? view.privateNotes }),
    active: patch.patch.active ?? view.active,
    appliedPatchIds: Object.freeze([...view.appliedPatchIds, patch.id]),
  });
}

/**
 * Apply only the selected company's patches in deterministic update/id order.
 * Patches never mutate the canonical record or its immutable digest.
 */
export function resolveCompanyEquipmentProductView(
  product: EquipmentLibraryProduct,
  patches: readonly CompanyEquipmentPatch[],
  companyId: string,
  revision?: EquipmentRevision,
): CompanyEquipmentProductView {
  const applicable = patches
    .filter(patch => patch.companyId === companyId && patch.targetProductId === product.id)
    .sort((left, right) => (
      left.updatedAt.localeCompare(right.updatedAt)
      || left.id.localeCompare(right.id)
    ));
  return applicable.reduce(
    (view, patch) => applyCompanyEquipmentPatch(view, patch, companyId),
    initialView(product, revision),
  );
}
